/**
 * Vendor configuration types and constants for Mobile Communication Send Load Options
 */

/** Unit of measurement types supported by vendors */
export type UnitOfMeasurement = 'feet' | 'meters' | 'miles' | 'km' | 'meters (less than depart)' | null;

/** Component visibility configuration for radius fields */
export interface RadiusConfiguration {
    arriving: boolean;
    arrived: boolean;
    depart: boolean;
}

/** Unit of measurement configuration for radius labels */
export interface UOMConfiguration {
    arriving: UnitOfMeasurement;
    arrived: UnitOfMeasurement;
    depart: UnitOfMeasurement;
}

export interface VendorPanelConfiguration {
    showDrvTechWeb?: boolean;
    showPeoplenet?: boolean;
    showQCWorkflow?: boolean;
    showPlatformScience?: boolean;
    showPegasus?: boolean;
    showBluetree?: boolean;
    showSymphony?: boolean;
}

/** Form component visibility configuration */
export interface FormConfiguration {
    showShipperForm: boolean;
    showStopForm: boolean;
    showDriverLoginForm?: boolean;
    showConsigneeForm: boolean;
    showEmptyShipperForm: boolean;
    showEmptyConsigneeForm: boolean;
    showArriveShipperForm: boolean;
    showArriveStopForm: boolean;
    showArriveConsigneeForm: boolean;
    showDepartShipperForm: boolean;
    showDepartStopForm: boolean;
    showDepartConsigneeForm: boolean;
}

/** Template component visibility configuration */
export interface TemplateConfiguration {
    dispatchTemplate: boolean;
    shipperTemplate: boolean;
    stopTemplate: boolean;
    consigneeTemplate: boolean;
}

/** Tab visibility configuration */
export interface TabConfiguration {
    general: boolean;
    radius: boolean;
    forms: boolean;
    templates: boolean;
    outerForms: boolean;
    tree: boolean;
    tripData: boolean;
}

/** Complete vendor configuration */
export interface VendorConfiguration {
    prefix: string;
    name: string;
    tabs: TabConfiguration;
    radius: RadiusConfiguration;
    uom: UOMConfiguration;
    vendorPanel: VendorPanelConfiguration;
    forms: FormConfiguration;
    templates: TemplateConfiguration;
    requiresLocationCheck: boolean;
    specialBehavior?: {
        arrivedRadiusCaption?: string;
        arriveShipperFormCaption?: string;
        arriveConsigneeFormCaption?: string;
        shipperFormCaption?: string;
        stopFormCaption?: string;
        consigneeFormCaption?: string;
        hideTemplatesTab?: boolean;
        hideFormsTab?: boolean;
        alwaysShowFormAndTree?: boolean;
    };
}

/** Vendor type enumeration */
// Keys sorted alphabetically (ascending) by enum key name
export enum VendorType {
    BLUE_TREE = 'BLU',
    CARRIER_WEB = 'CAR',
    DRIVER_TECH = 'DRV',
    FLEET_LOCATE = 'FLE',
    FORWARD_THINKING = 'FTS',
    IBRIGHT = 'IBR',
    KEEP_TRUCKIN = 'KEE',
    MAVEN = 'MAV',
    MCLEOD = 'MCL',
    OMNITRACS = 'OMN',
    PEDIGREE = 'PED',
    PEGASUS = 'PEG',
    PEOPLENET = 'PEO',
    PLATFORM_SCIENCE = 'PLA',
    QUALCOMM = 'QUA',
    QUALCOMM_WORKFLOW = 'QCW',
    RAND_DC = 'RAN',
    SAMSARA = 'SAM',
    SYMPHONY = 'SYM',
    TELETRAC = 'TEL',
    TRANZTEC = 'TRA',
    XRS = 'XRS'
}

/** Default configuration template */
const defaultConfig: Omit<VendorConfiguration, 'prefix' | 'name'> = {
    tabs: { general: true, radius: true, forms: false, templates: false, outerForms: false, tree: false, tripData: false },
    radius: { arriving: false, arrived: false, depart: false },
    uom: { arriving: null, arrived: null, depart: null },
    vendorPanel: { },
    forms: {
        showShipperForm: false, showStopForm: false, showConsigneeForm: false,
        showEmptyShipperForm: false, showEmptyConsigneeForm: false,
        showArriveShipperForm: false, showArriveStopForm: false, showArriveConsigneeForm: false,
        showDepartShipperForm: false, showDepartStopForm: false, showDepartConsigneeForm: false
    },
    templates: {
        dispatchTemplate: false, shipperTemplate: false,
        stopTemplate: false, consigneeTemplate: false
    },
    requiresLocationCheck: false
};

/** Vendor configurations mapping */
export const VENDOR_CONFIGURATIONS: Record<VendorType, VendorConfiguration> = {
    [VendorType.OMNITRACS]: {
        ...defaultConfig,
        prefix: 'OMNI',
        name: 'Omnitracs',
        tabs: { general: true, radius: true, forms: true, templates: true, outerForms: true, tree: false, tripData: false },
        radius: { arriving: false, arrived: true, depart: true },
        uom: { arriving: null, arrived: 'km', depart: 'km' },
        forms: {
            showShipperForm: false, showStopForm: false, showConsigneeForm: false,
            showEmptyShipperForm: false, showEmptyConsigneeForm: false,
            showArriveShipperForm: true, showArriveStopForm: false, showArriveConsigneeForm: true,
            showDepartShipperForm: false, showDepartStopForm: false, showDepartConsigneeForm: false
        },
        templates: {
            dispatchTemplate: true, shipperTemplate: true,
            stopTemplate: true, consigneeTemplate: true
        },
        requiresLocationCheck: true,
        specialBehavior: { arriveShipperFormCaption: 'Start Empty', arriveConsigneeFormCaption: 'End Empty' }
    },

    [VendorType.PEGASUS]: {
        ...defaultConfig,
        prefix: 'PEGA',
        name: 'Pegasus',
        tabs: { general: true, radius: true, forms: false, templates: false, outerForms: false, tree: false, tripData: false },
        radius: { arriving: true, arrived: true, depart: true },
        vendorPanel: { showPegasus: true },
        uom: { arriving: "miles", arrived: 'feet', depart: 'feet' }
    },

    [VendorType.PLATFORM_SCIENCE]: {
        ...defaultConfig,
        prefix: 'PLAT',
        name: 'Platform Science',
        tabs: { general: true, radius: true, forms: true, templates: true, outerForms: true, tree: true, tripData: false },
        radius: { arriving: false, arrived: true, depart: true },
        uom: { arriving: null, arrived: 'meters', depart: 'meters' },
        vendorPanel: { showPlatformScience: true },
        forms: {
            showShipperForm: false, showStopForm: false, showConsigneeForm: false,
            showEmptyShipperForm: false, showEmptyConsigneeForm: false,
            showArriveShipperForm: false, showArriveStopForm: false, showArriveConsigneeForm: false,
            showDepartShipperForm: true, showDepartStopForm: false, showDepartConsigneeForm: true
        },
        templates: {
            dispatchTemplate: true, shipperTemplate: true,
            stopTemplate: true, consigneeTemplate: true
        },
        requiresLocationCheck: true,
        specialBehavior: { arrivedRadiusCaption: 'Arrival Radius' }
    },

    [VendorType.BLUE_TREE]: {
        ...defaultConfig,
        prefix: 'BLUE',
        name: 'BlueTree',
        tabs: { general: true, radius: true, forms: true, templates: false, outerForms: false, tree: false, tripData: false },
        radius: { arriving: true, arrived: true, depart: true },
        uom: { arriving: 'km', arrived: 'km', depart: 'km' },
        vendorPanel: { showBluetree: true },
        forms: {
            showShipperForm: true, showStopForm: true, showConsigneeForm: true,
            showEmptyShipperForm: true, showEmptyConsigneeForm: true,
            showArriveShipperForm: false, showArriveStopForm: false, showArriveConsigneeForm: false,
            showDepartShipperForm: false, showDepartStopForm: false, showDepartConsigneeForm: false
        }
    },

    [VendorType.FORWARD_THINKING]: {
        ...defaultConfig,
        prefix: 'FTS',
        name: 'Forward Thinking Systems',
        tabs: { general: true, radius: true, forms: true, templates: false, outerForms: false, tree: false, tripData: false },
        radius: { arriving: false, arrived: true, depart: false },
        uom: { arriving: null, arrived: 'feet', depart: null },
        forms: {
            showShipperForm: true, showStopForm: true, showConsigneeForm: true,
            showEmptyShipperForm: false, showEmptyConsigneeForm: false,
            showArriveShipperForm: false, showArriveStopForm: false, showArriveConsigneeForm: false,
            showDepartShipperForm: false, showDepartStopForm: false, showDepartConsigneeForm: false
        }
    },

    [VendorType.IBRIGHT]: {
        ...defaultConfig,
        prefix: 'IBR',
        name: 'Ibright',
        uom: { arriving: null, arrived: 'meters', depart: 'meters' },
        radius: { arriving: false, arrived: true, depart: true },
        tabs: { general: true, radius: true, forms: false, templates: false, outerForms: false, tree: false, tripData: false }
    },

    [VendorType.MAVEN]: {
        ...defaultConfig,
        prefix: 'MAVE',
        name: 'Maven Machines',
        uom: { arriving: null, arrived: 'meters', depart: 'meters (less than depart)' },
        radius: { arriving: false, arrived: true, depart: true },
        tabs: { general: true, radius: true, forms: true, templates: false, outerForms: false, tree: false, tripData: false },
        forms: {
            showShipperForm: true, showStopForm: true, showConsigneeForm: true,
            showEmptyShipperForm: true, showEmptyConsigneeForm: true,
            showArriveShipperForm: false, showArriveStopForm: false, showArriveConsigneeForm: false,
            showDepartShipperForm: false, showDepartStopForm: false, showDepartConsigneeForm: false
        }
    },

    [VendorType.SYMPHONY]: {
        ...defaultConfig,
        prefix: 'SYM',
        name: 'Symphony',
        requiresLocationCheck: true,
        uom: { arriving: null, arrived: 'feet', depart: 'feet' },
        radius: { arriving: false, arrived: true, depart: true },
        vendorPanel: { showSymphony: true },
        tabs: { general: true, radius: true, forms: true, templates: true, outerForms: true, tree: false, tripData: false },
        forms: {
            showShipperForm: true, showStopForm: true, showConsigneeForm: true,
            showEmptyShipperForm: true, showEmptyConsigneeForm: true, showDriverLoginForm: true,
            showArriveShipperForm: true, showArriveStopForm: false, showArriveConsigneeForm: false,
            showDepartShipperForm: true, showDepartStopForm: true, showDepartConsigneeForm: true
        },
        templates: {
            dispatchTemplate: true, shipperTemplate: true,
            stopTemplate: true, consigneeTemplate: true
        },
        specialBehavior: { arriveShipperFormCaption: 'Fuel Form', shipperFormCaption: 'Arrive Shipper', stopFormCaption: 'Arrive Stop', consigneeFormCaption: 'Arrive Consignee' }
    },

    [VendorType.TRANZTEC]: {
        ...defaultConfig,
        prefix: 'TRAN',
        name: 'Tranztec',
        uom: { arriving: null, arrived: 'feet', depart: 'feet' },
        radius: { arriving: false, arrived: true, depart: true },
        tabs: { general: true, radius: true, forms: true, templates: false, outerForms: false, tree: false, tripData: false },
        forms: {
            showShipperForm: true, showStopForm: true, showConsigneeForm: true,
            showEmptyShipperForm: false, showEmptyConsigneeForm: false,
            showArriveShipperForm: false, showArriveStopForm: false, showArriveConsigneeForm: false,
            showDepartShipperForm: false, showDepartStopForm: false, showDepartConsigneeForm: false
        }
    },

    [VendorType.SAMSARA]: {
        ...defaultConfig,
        prefix: 'SAMSARA',
        name: 'Samsara',
        tabs: { general: true, radius: true, forms: true, templates: false, outerForms: false, tree: false, tripData: false },
        radius: { arriving: false, arrived: false, depart: true },
        uom: { arriving: null, arrived: null, depart: 'meters' },
        forms: {
            showShipperForm: true, showStopForm: true, showConsigneeForm: true,
            showEmptyShipperForm: true, showEmptyConsigneeForm: true,
            showArriveShipperForm: true, showArriveStopForm: true, showArriveConsigneeForm: true,
            showDepartShipperForm: true, showDepartStopForm: true, showDepartConsigneeForm: true
        }
    },

    [VendorType.MCLEOD]: {
        ...defaultConfig,
        prefix: 'MCL',
        name: 'McLeod Mobile',
        tabs: { general: true, radius: true, forms: true, templates: false, outerForms: true, tree: true, tripData: false },
        radius: { arriving: true, arrived: true, depart: true },
        uom: { arriving: null, arrived: 'feet', depart: 'feet' },
        forms: {
            showShipperForm: true, showStopForm: true, showConsigneeForm: true,
            showEmptyShipperForm: true, showEmptyConsigneeForm: true,
            showArriveShipperForm: false, showArriveStopForm: false, showArriveConsigneeForm: false,
            showDepartShipperForm: true, showDepartStopForm: true, showDepartConsigneeForm: true
        },
        specialBehavior: { shipperFormCaption: 'Arrive Shipper', stopFormCaption: 'Arrive Stop', consigneeFormCaption: 'Arrive Consignee' }
    },

    [VendorType.CARRIER_WEB]: {
        ...defaultConfig,
        prefix: 'CAR',
        name: 'CarrierWeb',
        tabs: { general: true, radius: true, forms: false, templates: false, outerForms: false, tree: false, tripData: false },
        radius: { arriving: false, arrived: true, depart: true },
        uom: { arriving: null, arrived: 'miles', depart: 'miles' }
    },

    [VendorType.FLEET_LOCATE]: {
        ...defaultConfig,
        prefix: 'FLEET',
        name: 'FleetLocate',
        tabs: { general: true, radius: true, forms: false, templates: false, outerForms: false, tree: false, tripData: false },
        radius: { arriving: false, arrived: true, depart: false },
        uom: { arriving: null, arrived: 'miles', depart: null }
    },

    [VendorType.PEOPLENET]: {
        ...defaultConfig,
        prefix: 'PEO',
        requiresLocationCheck: true,
        name: 'Peoplenet',
        tabs: { general: true, radius: true, forms: true, templates: false, outerForms: false, tree: false, tripData: true },
        radius: { arriving: true, arrived: true, depart: true },
        vendorPanel: { showPeoplenet: true },
        uom: { arriving: 'miles', arrived: 'miles', depart: 'miles' },
        forms: {
            showShipperForm: true, showStopForm: true, showConsigneeForm: true,
            showEmptyShipperForm: true, showEmptyConsigneeForm: true,
            showArriveShipperForm: false, showArriveStopForm: false, showArriveConsigneeForm: false,
            showDepartShipperForm: false, showDepartStopForm: false, showDepartConsigneeForm: false
        }
    },

    [VendorType.PEDIGREE]: {
        ...defaultConfig,
        prefix: 'PED',
        name: 'Pedigree',
        tabs: { general: true, radius: true, forms: true, templates: false, outerForms: false, tree: false, tripData: false },
        radius: { arriving: true, arrived: false, depart: false },
        uom: { arriving: 'miles', arrived: null, depart: null },
        forms: {
            showShipperForm: true, showStopForm: true, showConsigneeForm: true,
            showEmptyShipperForm: true, showEmptyConsigneeForm: true,
            showArriveShipperForm: false, showArriveStopForm: false, showArriveConsigneeForm: false,
            showDepartShipperForm: false, showDepartStopForm: false, showDepartConsigneeForm: false
        }
    },

    [VendorType.QUALCOMM_WORKFLOW]: {
        ...defaultConfig,
        prefix: 'QCWORK',
        name: 'Qualcomm Work Flow',
        tabs: { general: true, radius: true, forms: false, templates: false, outerForms: false, tree: false, tripData: false },
        radius: { arriving: false, arrived: true, depart: true },
        vendorPanel: { showQCWorkflow: true },
        uom: { arriving: null, arrived: 'feet', depart: 'feet' },
        specialBehavior: { hideTemplatesTab: true, hideFormsTab: true }
    },

    [VendorType.QUALCOMM]: {
        ...defaultConfig,
        prefix: 'Q',
        name: 'Qualcomm'
    },

    [VendorType.TELETRAC]: {
        ...defaultConfig,
        prefix: 'T',
        name: 'Teletrac'
    },

    [VendorType.DRIVER_TECH]: {
        ...defaultConfig,
        prefix: 'D',
        name: 'DriverTech',
        tabs: { general: true, radius: true, forms: true, templates: false, outerForms: false, tree: false, tripData: false },
        radius: { arriving: false, arrived: true, depart: false },
        vendorPanel: { showDrvTechWeb: true },
        uom: { arriving: null, arrived: 'meters', depart: null },
        forms: {
            showShipperForm: true, showStopForm: true, showConsigneeForm: true,
            showEmptyShipperForm: true, showEmptyConsigneeForm: true,
            showArriveShipperForm: false, showArriveStopForm: false, showArriveConsigneeForm: false,
            showDepartShipperForm: false, showDepartStopForm: false, showDepartConsigneeForm: false
        },
        specialBehavior: { arrivedRadiusCaption: 'Arrived/Departed Radius' }
    },

    [VendorType.XRS]: {
        ...defaultConfig,
        prefix: 'XRS',
        name: 'XRS',
        tabs: { general: true, radius: true, forms: true, templates: false, outerForms: false, tree: false, tripData: false },
        radius: { arriving: true, arrived: false, depart: true },
        uom: { arriving: 'feet', arrived: null, depart: 'feet' },
        forms: {
            showShipperForm: true, showStopForm: true, showConsigneeForm: true,
            showEmptyShipperForm: false, showEmptyConsigneeForm: false,
            showArriveShipperForm: false, showArriveStopForm: false, showArriveConsigneeForm: false,
            showDepartShipperForm: false, showDepartStopForm: false, showDepartConsigneeForm: false
        },
    },

    [VendorType.KEEP_TRUCKIN]: {
        ...defaultConfig,
        prefix: 'KEEP',
        name: 'Keep Truckin',
        tabs: { general: true, radius: true, forms: true, templates: false, outerForms: false, tree: false, tripData: false },
        radius: { arriving: false, arrived: true, depart: true },
        uom: { arriving: null, arrived: 'feet', depart: 'feet' },
        forms: {
            showShipperForm: true, showStopForm: true, showConsigneeForm: true,
            showEmptyShipperForm: true, showEmptyConsigneeForm: true,
            showArriveShipperForm: true, showArriveStopForm: true, showArriveConsigneeForm: true,
            showDepartShipperForm: true, showDepartStopForm: true, showDepartConsigneeForm: true
        },
        specialBehavior: { hideTemplatesTab: true }
    },

    [VendorType.RAND_DC]: {
        ...defaultConfig,
        prefix: 'RAND',
        name: 'Rand DC',
        tabs: { general: true, radius: false, forms: true, templates: false, outerForms: false, tree: false, tripData: false },
        forms: {
            showShipperForm: true, showStopForm: true, showConsigneeForm: true,
            showEmptyShipperForm: true, showEmptyConsigneeForm: true,
            showArriveShipperForm: true, showArriveStopForm: true, showArriveConsigneeForm: true,
            showDepartShipperForm: false, showDepartStopForm: false, showDepartConsigneeForm: false
        },
    }
};
